import { useState } from "react";
import { toast } from "sonner";
import { Phone, PhoneMissed } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { missedCall } from "./WhatsAppDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const dayPlus = (n) => new Date(Date.now() + n * 864e5).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const OUTCOMES = [
  { k: "interested", label: "Interested", text: "Spoke: interested", days: 2 },
  { k: "quote", label: "Send quote", text: "Spoke: asked for a quote", days: 3 },
  { k: "later", label: "Call later", text: "Spoke: asked to call back later", days: 1 },
  { k: "busy", label: "Busy, call tomorrow", text: "Spoke briefly: busy, call tomorrow", days: 1 },
  { k: "not", label: "Not interested", text: "Spoke: not interested", days: null },
];

export const canDial = (l) => l.phone && !l._masked?.includes("phone") && !l._restricted?.includes("phone");

const LogSheet = ({ l, onClose, onDone }) => {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(null);
  const choose = (o) => { setPick(o.k); setText(o.text); setNext(o.days == null ? "" : dayPlus(o.days)); };
  const done = () => { toast.dismiss(`cb-/sales/leads/${l.lead_id}`); window.dispatchEvent(new Event("callbacks-changed")); onDone?.(); onClose(); };
  const save = () => {
    setBusy(true);
    api.post(`/leads/${l.lead_id}/activities`, { type: "call", text: text.trim(), next_contact_date: next || null })
      .then(() => { toast.success("Call logged"); done(); }).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  };
  const name = l.contact_name || l.company_name || l.lead_id;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card max-w-md" data-testid="quick-log-sheet">
        <DialogHeader><DialogTitle className="font-display">How did the call with {name} go?</DialogTitle></DialogHeader>
        <button data-testid="quick-log-missed" onClick={() => { missedCall(l, user?.staff_name || user?.name || "", done); }}
          className="h-11 border border-amber-500/60 text-amber-600 text-sm flex items-center justify-center gap-2"><PhoneMissed className="h-4 w-4" />Didn't pick up (WhatsApp + callback)</button>
        <div className="text-xs text-muted-foreground text-center">or, if you spoke</div>
        <div className="flex flex-wrap gap-2" data-testid="quick-log-outcomes">
          {OUTCOMES.map((o) => (
            <button key={o.k} data-testid={`quick-log-outcome-${o.k}`} onClick={() => choose(o)}
              className={`h-9 px-3 text-sm border rounded-full transition-colors ${pick === o.k ? "bg-primary text-primary-foreground border-primary" : "hover:border-primary"}`}>{o.label}</button>))}
        </div>
        <textarea data-testid="quick-log-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Pick an answer above or type what they said" className="bg-muted/60 border p-2 text-sm h-20 w-full" />
        <label className="text-xs flex items-center gap-2"><span className="ax-label">Next follow-up</span>
          <input type="date" data-testid="quick-log-next" value={next} onChange={(e) => setNext(e.target.value)} className="bg-muted/60 border h-9 px-2 text-sm flex-1" /></label>
        <div className="flex gap-2">
          <button data-testid="quick-log-skip" onClick={onClose} className="flex-1 h-11 border text-sm">Not now</button>
          <button data-testid="quick-log-save" disabled={!text.trim() || busy} onClick={save} className="flex-1 h-11 bg-primary text-primary-foreground text-sm disabled:opacity-50">{busy ? "Saving…" : "Save call"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CallButton = ({ l, testid, onDone, className = "" }) => {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  if (!canDial(l)) return null;
  const logs = can("sales", "update");
  return (
    <>
      <a href={`tel:${l.phone}`} data-testid={testid} aria-label={`Call ${l.contact_name || l.company_name}`} onClick={() => { if (logs) setTimeout(() => setOpen(true), 400); }}
        className={`self-center shrink-0 h-11 w-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center active:scale-95 transition-transform ${className}`}><Phone className="h-5 w-5" /></a>
      {open && <LogSheet l={l} onClose={() => setOpen(false)} onDone={onDone} />}
    </>
  );
};
